import { onMounted, onUnmounted, ref } from 'vue';

const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000;
const DEFAULT_VERSION_URL = '/version.json';

interface VersionCheckOptions {
  currentVersion: string;
  pollIntervalMs?: number;
  versionUrl?: string;
}

/**
 * Polls a static version.json (written at build time by versionJsonPlugin) and
 * flips `updateAvailable` once the server reports a version different from the
 * one this session was built with. Catches the case Cache-Control headers
 * can't: a tab left open across a deploy never makes a new request for
 * index.html on its own, so it never finds out a new build exists.
 */
export const useVersionCheck = ({
  currentVersion,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  versionUrl = DEFAULT_VERSION_URL,
}: VersionCheckOptions) => {
  const updateAvailable = ref(false);
  let intervalId: ReturnType<typeof setInterval> | undefined;

  const checkVersion = async () => {
    if (!currentVersion || updateAvailable.value) {
      return;
    }
    try {
      const res = await fetch(versionUrl, { cache: 'no-store' });
      if (!res.ok) {
        return;
      }
      const data = await res.json();
      if (data?.version && data.version !== currentVersion) {
        updateAvailable.value = true;
      }
    } catch {
      // Transient network errors aren't worth surfacing - just try again next interval.
    }
  };

  onMounted(() => {
    intervalId = setInterval(checkVersion, pollIntervalMs);
  });

  onUnmounted(() => {
    if (intervalId) {
      clearInterval(intervalId);
    }
  });

  const reload = () => {
    window.location.reload();
  };

  return { updateAvailable, reload };
};
